<template>
  <h3 class="tw:mt-4">
    {{ $t("modals.saml_integration_modal.associated_domains") }}
  </h3>
  <div class="tw:mt-4 flex">
    <InputText
      v-model="domain"
      class="tw:mr-2 tw:w-64"
      new-design
      :placeholder="
        $t('modals.saml_integration_modal.associated_domains_placeholder')
      "
    />
    <BaseButton
      color="secondary"
      :label="$t('global.add')"
      :loading="isLoading"
      new-design
      @click="onAddDomain"
    />
  </div>

  <p class="tw:mt-4">
    {{ $t("modals.saml_integration_modal.associated_domains_description") }}
  </p>

  <div
    v-if="Object.keys(samlIntegration.domains || {}).length"
    class="tw:mt-4 tw:rounded-lg tw:border tw:border-solid tw:border-gray-200 mx-auto w-full max-w-lg"
  >
    <div>
      <div class="tw:p-2 tw:font-bold tw:text-denim-700">
        <div class="tw:grid tw:grid-cols-[0.5fr_1fr_1fr_0.1fr]">
          <div>
            {{ $t("modals.saml_integration_modal.status") }}
          </div>
          <div>
            {{ $t("modals.saml_integration_modal.domain") }}
          </div>
          <div>
            {{ $t("modals.saml_integration_modal.txt") }}
          </div>
          <div />
        </div>
      </div>
    </div>

    <BaseSeparator />

    <div class="tw:mx-2">
      <div
        v-for="(domainAttributes, domainName, index) in samlIntegration.domains"
        :key="index"
      >
        <div
          class="tw:grid tw:grid-cols-[0.5fr_1fr_1fr_0.1fr] tw:items-center tw:py-2"
        >
          <div
            class="tw:mr-6 tw:border tw:border-solid tw:p-1 tw:text-center"
            :class="{
              'tw:border-green-700 tw:bg-green-200 tw:text-green-700':
                domainAttributes.verified,
              'tw:border-red-700 tw:bg-red-200 tw:text-red-700':
                !domainAttributes.verified,
            }"
          >
            {{
              domainAttributes.verified
                ? $t("modals.saml_integration_modal.verified")
                : $t("modals.saml_integration_modal.unverified")
            }}
          </div>
          <div class="font-medium text-gray-900">{{ domainName }}</div>
          <InputText
            borderless
            class="tw:mr-14 tw:w-56"
            disabled
            :model-value="domainAttributes.key"
            new-design
          >
            <template #append>
              <BaseButton
                :aria-label="$t('global.copy_to_clipboard')"
                class="tw:border-none!"
                flat
                icon="copy"
                icon-size="lg"
                new-design
                new-icon
                @click="copyValueToClipboard(domainAttributes.key)"
              />
            </template>
          </InputText>

          <BaseButtonDropdown
            close-on-click
            flat
            icon="more_vert"
            menu-item
            new-dropdown
            new-icon
            padding="xs"
            round
          >
            <BaseButton
              icon="delete"
              :label="$t('global.delete')"
              new-design
              @click="onRemoveDomain(domainName)"
            />
          </BaseButtonDropdown>
        </div>
      </div>
    </div>
  </div>
  <BaseButton
    v-if="Object.keys(samlIntegration.domains || {}).length"
    class="tw:ml-auto tw:mt-4"
    color="primary"
    :label="$t('modals.saml_integration_modal.verify_domains')"
    :loading="isLoading"
    new-design
    @click="() => props.onVerifyDomains()"
  />
</template>

<script setup lang="ts">
import { ref } from "vue";

import {
  BaseButton,
  BaseButtonDropdown,
  BaseSeparator,
  InputText,
} from "shared/components/base";
import type { SamlIntegration } from "shared/resources";

const { copyToClipboard } = useClipboard();
import useClipboard from "shared/composables/useClipboard";

const domain = ref<string>("");

const samlIntegration = defineModel<SamlIntegration>({
  required: true,
});

const props = defineProps({
  onSaveIntegration: { type: Function, required: true },
  onVerifyDomains: { type: Function, required: true },
  isLoading: { type: Boolean, required: true },
});

function onAddDomain() {
  if (!samlIntegration.value.domains) {
    samlIntegration.value.domains = {};
  }

  samlIntegration.value.domains[domain.value] = {};
  domain.value = "";
  props.onSaveIntegration();
}

function onRemoveDomain(domainName: string) {
  delete samlIntegration.value.domains[domainName];
  props.onSaveIntegration();
}

function copyValueToClipboard(value: string) {
  copyToClipboard(value);
}
</script>
